import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TILE_IMAGES } from "@/lib/tiles";
import { LOTUS_MASK_URL } from "@/lib/lotus";
import Lightbox from "@/components/Lightbox";

// A fresh arrangement on every visit — pure browser-side randomness, so it
// works identically on any static host. A repair pass then makes sure photos
// from the same shoot never sit near each other.
const randomShuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const SPREAD = 14; // same-shoot tiles must sit at least this many cells apart
const spreadShoots = (arr) => {
  const a = [...arr];
  const clashesAt = (pos) => {
    for (let d = 1; d <= SPREAD; d++) {
      const n = a[pos - d];
      if (n && n.shoot === a[pos].shoot) return true;
    }
    return false;
  };
  const wouldClash = (pos, item) => {
    for (let d = 1; d <= SPREAD; d++) {
      const n = a[pos - d];
      const m = a[pos + d];
      if (n && n.shoot === item.shoot) return true;
      if (m && m.shoot === item.shoot) return true;
    }
    return false;
  };
  for (let i = 0; i < a.length; i++) {
    if (!clashesAt(i)) continue;
    for (let j = i + 1; j < a.length; j++) {
      if (!wouldClash(i, a[j]) && !wouldClash(j, a[i])) {
        [a[i], a[j]] = [a[j], a[i]];
        break;
      }
    }
  }
  return a;
};

const FILLER_TONES = ["#f2ecdd", "#efe8d6", "#f4efe2", "#ede6d2"];

const CENTER_TILE = "/collage/center4.webp"; // Amrita's chosen centre image — original coloration
const SHADOW_TILE = "/collage/shadow2.webp"; // the gold portrait, resting in the dark field

const maskStyle = {
  WebkitMaskImage: LOTUS_MASK_URL,
  maskImage: LOTUS_MASK_URL,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center 60%",
  maskPosition: "center 60%",
  WebkitMaskSize: "min(92vw, 108vh)",
  maskSize: "min(92vw, 108vh)",
};

const TileGrid = ({ tiles, bright, reduceMotion, className = "", onTileClick = null }) => (
  <div
    aria-hidden={onTileClick ? undefined : "true"}
    className={`absolute inset-0 grid content-start ${className}`}
    style={{
      gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))",
      gridAutoRows: "104px",
      gap: "3px",
      ...(bright ? maskStyle : {}),
    }}
  >
    {tiles.map((src, i) => {
      // painterly per-tile jitter so the field reads hand-made, like a Monet
      const jitter = ((i * 2654435761) % 100) / 100;
      if (!src) {
        // pool exhausted — the field dissolves into darkness
        return (
          <div
            key={`${bright ? "b" : "d"}-f${i}`}
            aria-hidden="true"
            style={{ background: FILLER_TONES[Math.floor(jitter * FILLER_TONES.length)] }}
          />
        );
      }
      const darkFilter = `brightness(${1.62 + jitter * 0.22}) saturate(0.15) contrast(0.9)`;
      return (
        <motion.div
          key={`${bright ? "b" : "d"}-${i}`}
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: reduceMotion ? 0 : (bright ? 0.7 : 0) + ((i * 7919) % 160) / 100,
            duration: 1.4,
            ease: "easeOut",
          }}
          className={`overflow-hidden ${onTileClick ? "cursor-pointer" : ""}`}
          style={{ background: "#efe8d4" }}
          onClick={onTileClick ? () => onTileClick(i) : undefined}
          data-testid={onTileClick ? `collage-tile-${i}` : undefined}
        >
          <img
            src={src}
            alt=""
            draggable="false"
            className="h-full w-full object-cover"
            style={
              bright
                ? { filter: `brightness(${0.98 + jitter * 0.08}) saturate(1.12) contrast(1.03)` }
                : { filter: darkFilter }
            }
          />
        </motion.div>
      );
    })}
  </div>
);

const LotusMatrix = () => {
  const reduceMotion = useReducedMotion();
  const heroRef = useRef(null);
  const [pins, setPins] = useState({ center: null, shadow: null, cells: 160 });
  const [openTile, setOpenTile] = useState(null);

  // Find the grid cell that sits at the heart of the lotus and pin Amrita's
  // chosen image there; the gold portrait rests in a shadowed corner cell.
  useLayoutEffect(() => {
    const measure = () => {
      const el = heroRef.current;
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      const cell = 107; // 104px cell + 3px gap
      const cols = Math.max(1, Math.floor((w + 3) / cell));
      const rows = Math.max(1, Math.ceil((h + 3) / cell));
      const centerRow = Math.max(0, Math.round((0.58 * h) / cell));
      const shadowRow = Math.max(0, Math.round((0.78 * h) / cell));
      setPins({
        center: centerRow * cols + Math.floor(cols / 2),
        shadow: shadowRow * cols + Math.floor(cols * 0.12),
        cells: rows * cols,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const tiles = useMemo(() => {
    const pool = spreadShoots(randomShuffle(TILE_IMAGES)).map((t) => t.src);
    const out = pool.slice(0, pins.cells);
    while (out.length < pins.cells) out.push(null); // fade to darkness
    if (pins.center !== null && pins.center < out.length) out[pins.center] = CENTER_TILE;
    if (pins.shadow !== null && pins.shadow < out.length && pins.shadow !== pins.center)
      out[pins.shadow] = SHADOW_TILE;
    return out;
  }, [pins]);

  return (
    <section
      ref={heroRef}
      data-testid="lotus-hero"
      className="relative h-[100svh] min-h-[560px] w-full overflow-hidden"
      style={{ background: "#f6f1e5" }}
    >
      {/* warm paper glow — light pooled where the lotus will ink in */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 50% 62%, rgba(212,175,55,0.16) 0%, rgba(214,188,140,0.10) 45%, rgba(246,241,229,0) 75%), radial-gradient(ellipse 90% 70% at 50% 110%, rgba(176,110,60,0.10) 0%, rgba(246,241,229,0) 60%)",
        }}
      />

      {/* the dark field — every tile dimmed into an iridescent shadow (clickable) */}
      <TileGrid tiles={tiles} bright={false} reduceMotion={reduceMotion} onTileClick={setOpenTile} />

      {/* cream veil over the field so it reads as faint impressions on paper */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(160deg, rgba(246,241,229,0.42) 0%, rgba(246,241,229,0.34) 55%, rgba(240,232,214,0.45) 100%)",
        }}
      />

      {/* soft emerald bloom behind the lotus silhouette — breathing */}
      <div
        aria-hidden="true"
        className="lotus-bloom-pulse pointer-events-none absolute inset-0"
        style={{
          ...maskStyle,
          background:
            "radial-gradient(ellipse at center 60%, rgba(212,175,55,0.55) 0%, rgba(176,110,60,0.2) 55%, rgba(246,241,229,0) 80%)",
          filter: "blur(28px)",
          transform: "scale(1.04)",
        }}
      />

      {/* gold sheen travelling around the petal boundary */}
      <div
        aria-hidden="true"
        className="lotus-sheen pointer-events-none absolute inset-0"
        style={{
          ...maskStyle,
          filter: "blur(22px)",
          transform: "scale(1.02)",
        }}
      />

      {/* the same collage, full colour, revealed only inside the lotus — breathing */}
      <TileGrid
        tiles={tiles}
        bright
        reduceMotion={reduceMotion}
        className={`pointer-events-none ${reduceMotion ? "" : "lotus-breathe"}`}
      />

      {/* edges fall away to near-black; the light pools around the lotus */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 60% at 50% 52%, rgba(246,241,229,0) 34%, rgba(70,50,22,0.28) 56%, rgba(26,16,7,0.68) 78%, rgba(8,5,2,0.92) 93%, rgba(5,3,2,0.97) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, rgba(20,13,6,0.35) 0%, rgba(246,241,229,0) 16%, rgba(246,241,229,0) 78%, rgba(10,6,3,0.55) 100%)",
        }}
      />

      {/* masthead — the only type on the art */}
      <header className="pointer-events-none absolute inset-x-0 top-0 z-10 flex flex-col items-center pt-[5vh]">
        <motion.h1
          data-testid="masthead-title"
          initial={reduceMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1.6, ease: "easeOut" }}
          className="font-masthead gold-text text-center uppercase leading-[0.9]"
          style={{
            fontSize: "clamp(2.6rem, 10.5vw, 10rem)",
            letterSpacing: "0.09em",
            fontWeight: 500,
            WebkitTextStroke: "1.5px rgba(15,10,5,0.85)",
            filter:
              "drop-shadow(0 0 10px rgba(10,6,3,0.85)) drop-shadow(0 2px 26px rgba(10,6,3,0.75)) drop-shadow(0 4px 60px rgba(10,6,3,0.45))",
          }}
        >
          Amrita Sen
        </motion.h1>
        <motion.p
          data-testid="masthead-subtitle"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1.8, ease: "easeOut" }}
          className="font-serif-display mt-[2.2vh] rounded-full px-8 py-2 text-center italic text-[#6b5418]"
          style={{
            fontSize: "clamp(0.95rem, 1.6vw, 1.5rem)",
            letterSpacing: "0.42em",
            background:
              "radial-gradient(ellipse 100% 140% at 50% 50%, rgba(246,241,229,0.92) 30%, rgba(246,241,229,0) 75%)",
          }}
        >
          live creatively
        </motion.p>
      </header>

      {/* quiet scroll cue */}
      <motion.a
        data-testid="scroll-cue"
        href="#elsewhere"
        aria-label="Scroll to links"
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 0.55 }}
        transition={{ delay: 3, duration: 1.5 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-[#7a7160] transition-colors duration-500 hover:text-[#8a6a1f]"
      >
        <svg width="18" height="30" viewBox="0 0 18 30" fill="none" aria-hidden="true">
          <line x1="9" y1="2" x2="9" y2="24" stroke="currentColor" strokeWidth="1" />
          <path d="M3 19 L9 26 L15 19" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      </motion.a>

      <Lightbox tile={openTile !== null ? tiles[openTile] : null} onClose={() => setOpenTile(null)} />
    </section>
  );
};

export default LotusMatrix;
